import React, { useState, useRef } from 'react';
import { useClients } from '../hooks/useClients';
import { useToast } from '../context/ToastContext';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Modal } from '../components/common/Modal';
import type { Client } from '../types';

export const Database: React.FC = () => {
  const { clients, loading, createClient, updateClient, deleteClient, searchClients } = useClients();
  const { showToast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    status: 'active' as Client['status'],
    source: 'manual' as Client['source'],
    notes: '',
  });

  const getFilteredClients = () => {
    let filtered = clients;

    if (searchQuery) {
      filtered = searchClients(searchQuery);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    if (sourceFilter !== 'all') {
      filtered = filtered.filter(c => c.source === sourceFilter);
    }

    return filtered;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const clientData = {
        ...formData,
      };

      if (editingClient) {
        await updateClient(editingClient.id, clientData);
        showToast('Cliente atualizado com sucesso!', 'success');
      } else {
        await createClient(clientData);
        showToast('Cliente cadastrado com sucesso!', 'success');
      }

      setIsModalOpen(false);
      resetForm();
    } catch (error: any) {
      showToast(error.message || 'Erro ao salvar cliente', 'error');
    }
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email || '',
      phone: client.phone || '',
      company: client.company || '',
      status: client.status,
      source: client.source,
      notes: client.notes || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir este cliente?')) {
      try {
        await deleteClient(id);
        showToast('Cliente excluído com sucesso!', 'success');
      } catch (error: any) {
        showToast(error.message || 'Erro ao excluir cliente', 'error');
      }
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      status: 'active',
      source: 'manual',
      notes: '',
    });
  };

  const getStatusLabel = (status: Client['status']) => {
    const labels = {
      'active': 'Ativo',
      'inactive': 'Inativo',
      'lead': 'Lead',
      'prospect': 'Prospect'
    };
    return labels[status] || status;
  };

  const getStatusBadgeClass = (status: Client['status']) => {
    const classes = {
      'active': 'badge-success',
      'inactive': 'badge-warning',
      'lead': 'badge-info',
      'prospect': 'badge-info'
    };
    return classes[status] || 'badge-info';
  };

  const getSourceLabel = (source: Client['source']) => {
    const labels = {
      'manual': 'Manual',
      'csv_import': 'CSV',
      'json_import': 'JSON',
      'website': 'Website',
      'referral': 'Indicação',
      'social_media': 'Redes Sociais'
    };
    return labels[source] || source;
  };

  const getAvatarInitials = (name: string) => {
    if (!name) return '👤';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const stats = {
    total: clients.length,
    active: clients.filter(c => c.status === 'active').length,
    leads: clients.filter(c => c.status === 'lead').length,
    withEmail: clients.filter(c => c.email).length,
  };

  if (loading) {
    return <div className="text-center py-8">Carregando clientes...</div>;
  }

  const filteredClients = getFilteredClients();

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-medium">Base de Dados</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => showToast('Funcionalidade de importação em desenvolvimento', 'info')}>
            📥 Importar
          </Button>
          <Button variant="outline" onClick={() => showToast('Funcionalidade de exportação em desenvolvimento', 'info')}>
            📤 Exportar
          </Button>
          <Button onClick={() => { resetForm(); setIsModalOpen(true); }}>
            + Novo Cliente
          </Button>
        </div>
      </div>

      <div className="database-filters card">
        <div className="flex flex-wrap gap-4">
          <Input
            placeholder="Buscar clientes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 min-w-64"
          />
          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Todos os Status</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
            <option value="lead">Leads</option>
            <option value="prospect">Prospects</option>
          </select>
          <select
            className="form-select"
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
          >
            <option value="all">Todas as Fontes</option>
            <option value="manual">Manual</option>
            <option value="csv_import">CSV</option>
            <option value="json_import">JSON</option>
            <option value="website">Website</option>
            <option value="referral">Indicação</option>
            <option value="social_media">Redes Sociais</option>
          </select>
        </div>
      </div>

      <div className="database-stats grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="stat-card">
          <div className="stat-info">
            <h3 className="text-2xl font-medium">{stats.total}</h3>
            <p className="text-text-secondary text-sm">Total</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <h3 className="text-2xl font-medium">{stats.active}</h3>
            <p className="text-text-secondary text-sm">Ativos</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <h3 className="text-2xl font-medium">{stats.leads}</h3>
            <p className="text-text-secondary text-sm">Leads</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-info">
            <h3 className="text-2xl font-medium">{stats.withEmail}</h3>
            <p className="text-text-secondary text-sm">Com Email</p>
          </div>
        </div>
      </div>

      {filteredClients.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-text-secondary">Nenhum cliente encontrado</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map((client) => (
            <div key={client.id} className="client-card">
              <div className="client-header flex items-start gap-3 mb-3">
                <div className="client-avatar w-10 h-10 rounded-full bg-accent text-white flex items-center justify-center font-medium">
                  {getAvatarInitials(client.name)}
                </div>
                <div className="client-info flex-1">
                  <h3 className="font-medium">{client.name}</h3>
                  <p className="client-email text-sm text-text-secondary">
                    {client.email || 'Sem email'}
                  </p>
                </div>
                <span className={`badge ${getStatusBadgeClass(client.status)}`}>
                  {getStatusLabel(client.status)}
                </span>
              </div>
              <div className="client-details space-y-2 text-sm mb-4">
                {client.phone && <p className="text-text-secondary">📱 {client.phone}</p>}
                {client.company && <p className="text-text-secondary">🏢 {client.company}</p>}
                {client.source && <p className="text-text-secondary">🏷️ {getSourceLabel(client.source)}</p>}
              </div>
              <div className="client-actions flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleEdit(client)}>
                  ✏️
                </Button>
                <Button variant="danger" size="sm" onClick={() => handleDelete(client.id)}>
                  🗑️
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingClient ? 'Editar Cliente' : 'Novo Cliente'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancelar</Button>
            <Button onClick={() => formRef.current?.requestSubmit()}>{editingClient ? 'Atualizar' : 'Salvar'}</Button>
          </>
        }
      >
        <form ref={formRef} onSubmit={handleSubmit}>
          <Input
            label="Nome"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
          <Input
            label="Telefone"
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
          <Input
            label="Empresa"
            value={formData.company}
            onChange={(e) => setFormData({ ...formData, company: e.target.value })}
          />
          <div className="form-group">
            <label className="form-label">Status</label>
            <select
              className="form-select w-full"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as Client['status'] })}
            >
              <option value="active">Ativo</option>
              <option value="inactive">Inativo</option>
              <option value="lead">Lead</option>
              <option value="prospect">Prospect</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Fonte</label>
            <select
              className="form-select w-full"
              value={formData.source}
              onChange={(e) => setFormData({ ...formData, source: e.target.value as Client['source'] })}
            >
              <option value="manual">Manual</option>
              <option value="csv_import">CSV</option>
              <option value="json_import">JSON</option>
              <option value="website">Website</option>
              <option value="referral">Indicação</option>
              <option value="social_media">Redes Sociais</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Notas</label>
            <textarea
              className="input w-full"
              rows={4}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </div>
        </form>
      </Modal>
    </div>
  );
};
